/**
 * Step 2 of the loan tape onboarding (DOP-627): the new file to clean.
 *
 * Three ways in, as the canvas drew them: drop it (it is added to the
 * example's deal and picked once Doppler has read it), choose it from the
 * Deal Room, or take one of the other workbooks already in that deal.
 */

import { Loader2, Upload } from "lucide-react";
import React, { useRef, useState } from "react";
import type { SchemaExtractorDocRef } from "../SchemaExtractorForm";
import type { CatalogDocument } from "../../hooks/useDocumentMention";
import { useLoanTapeFileDrop } from "../../hooks/useLoanTapeFileDrop";
import { useWorkbookSearch } from "../../hooks/useWorkbookSearch";
import WorkbookCard from "./WorkbookCard";
import WorkbookSearch from "./WorkbookSearch";

/** How many of the deal's other workbooks are offered as suggestions. */
const SUGGESTIONS = 3;

export interface NewFileStepProps {
	value: SchemaExtractorDocRef | null;
	onChange: (doc: CatalogDocument) => void;
	/** The deal the example's raw file is in: where a dropped file goes. */
	projectId: string | null | undefined;
	dealName: string | null | undefined;
	/** The example's two files, never offered as the new one. */
	exclude: readonly string[];
}

const NewFileStep: React.FC<NewFileStepProps> = ({
	value,
	onChange,
	projectId,
	dealName,
	exclude,
}) => {
	const [choosing, setChoosing] = useState(false);
	const [dragging, setDragging] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const { state, drop, reset } = useLoanTapeFileDrop(projectId, onChange);
	const { results } = useWorkbookSearch("", Boolean(projectId) && !value, projectId);
	const suggestions = results
		.filter((doc) => !exclude.includes(doc.doc_id))
		.slice(0, SUGGESTIONS);

	if (value) {
		return (
			<WorkbookCard
				kind="The new file"
				icon="raw"
				value={value}
				onChange={onChange}
				exclude={exclude}
			/>
		);
	}

	if (choosing) {
		return (
			<WorkbookSearch
				label="Find the new file"
				exclude={exclude}
				autoFocus
				onPick={(doc) => {
					onChange(doc);
					setChoosing(false);
				}}
				onCancel={() => setChoosing(false)}
			/>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			<div
				onDragOver={(event) => {
					event.preventDefault();
					setDragging(true);
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={(event) => {
					event.preventDefault();
					setDragging(false);
					drop(Array.from(event.dataTransfer.files));
				}}
				data-testid="new-file-drop"
				className={`flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] border-dashed px-[18px] py-4 ${
					dragging
						? "border-[#004C4B] bg-[#E6F3F3]"
						: "border-[#99CFCD] bg-[#f6fbfb]"
				}`}
			>
				{state.kind === "adding" ? (
					<span className="flex items-center gap-2 text-sm text-[#1F2937]">
						<Loader2 className="h-4 w-4 animate-spin text-[#004C4B]" aria-hidden />
						Adding <b>{state.filename}</b> to {state.dealName}. It's picked here
						as soon as Doppler has read it.
					</span>
				) : (
					<>
						<Upload className="h-5 w-5 text-[#004C4B]" aria-hidden />
						<span className="text-sm text-[#1F2937]">Drop the file here, or</span>
						<button
							type="button"
							onClick={() => setChoosing(true)}
							className="inline-flex h-[34px] items-center rounded-lg border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-[#1F2937] hover:bg-gray-50"
						>
							Choose from the Deal Room
						</button>
						<button
							type="button"
							onClick={() => inputRef.current?.click()}
							className="text-[12.5px] font-medium text-[#004C4B] underline underline-offset-[3px]"
						>
							upload from your computer
						</button>
						<input
							ref={inputRef}
							type="file"
							accept=".xlsx"
							className="hidden"
							aria-label="Upload the new file"
							onChange={(event) => {
								drop(Array.from(event.target.files ?? []));
								event.target.value = "";
							}}
						/>
					</>
				)}
			</div>
			{state.kind === "failed" && (
				<p role="alert" className="text-[12.5px] text-[#b42318]">
					{state.message}{" "}
					<button
						type="button"
						onClick={reset}
						className="font-medium underline underline-offset-[3px]"
					>
						OK
					</button>
				</p>
			)}
			{suggestions.length > 0 && state.kind !== "adding" && (
				<div className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-[#5B6472]">
					<span>Also in {dealName ?? "this deal"}:</span>
					{suggestions.map((doc) => (
						<button
							key={doc.doc_id}
							type="button"
							onClick={() => onChange(doc)}
							className="inline-flex h-7 items-center rounded-md border border-[#CCE7E6] bg-white px-2.5 font-medium text-[#004C4B] hover:bg-[#f6fbfb]"
						>
							{doc.filename}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default NewFileStep;
